"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { AlertTriangle, CalendarPlus, CheckCircle2, Clock, Loader2, Video, XCircle } from "lucide-react";
import type { AppLocale } from "@/core/i18n/config";
import { localizeHref } from "@/core/i18n/routing";
import { normalizeAppError } from "@/shared/lib/async-result";
import { getBookingStatus } from "../../data/repositories/booking.repository";
import type { Booking } from "../../types/booking";
import { buildGoogleCalendarUrl, buildIcsEvent } from "../../domain/services/ics-builder";
const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 60_000;
const JOIN_WINDOW_MS = 5 * 60 * 1000;
const MODE_LABEL: Record<string, {
  en: string;
  hi: string;
}> = {
  VIDEO: {
    en: "Video",
    hi: "वीडियो"
  },
  AUDIO: {
    en: "Audio",
    hi: "ऑडियो"
  },
  CHAT: {
    en: "Chat",
    hi: "चैट"
  }
};
function formatSlotDate(iso: string, locale: AppLocale): string {
  try {
    return new Intl.DateTimeFormat(locale === "hi" ? "hi-IN" : "en-IN", {
      weekday: "long",
      day: "numeric",
      month: "long",
      hour: "numeric",
      minute: "2-digit",
      hour12: true
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}
function downloadIcs(booking: Booking) {
  const ics = buildIcsEvent({
    uid: `booking-${booking.id}@itshealer`,
    title: "ItsHealer session",
    description: booking.googleMeetLink ? `Join: ${booking.googleMeetLink}` : undefined,
    startDatetime: booking.startDatetime,
    endDatetime: booking.endDatetime
  });
  const blob = new Blob([ics], {
    type: "text/calendar;charset=utf-8"
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `itshealer-session-${booking.id}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
function getErrorMessage(error: unknown): string {
  return normalizeAppError(error).message;
}
export function BookingConfirmationPage({
  bookingId,
  healerBasePath = "/healers",
  locale
}: {
  bookingId: string;
  healerBasePath?: string;
  locale: AppLocale;
}) {
  const isHindi = locale === "hi";
  const [booking, setBooking] = useState<Booking | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const startedAtRef = useRef<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    startedAtRef.current = Date.now();
    async function poll() {
      try {
        const result = await getBookingStatus(bookingId);
        if (cancelled) return;
        setBooking(result);
        const isTerminal = result.status === "CONFIRMED" || result.status === "PAYMENT_FAILED" || result.status === "EXPIRED" || result.status === "CANCELLED";
        if (isTerminal) {
          return;
        }
        if (Date.now() - (startedAtRef.current ?? Date.now()) >= POLL_TIMEOUT_MS) {
          setTimedOut(true);
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (caughtError) {
        if (cancelled) return;
        setError(getErrorMessage(caughtError));
      }
    }
    void poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [bookingId]);

  // Live tick so the "Join Session" button unlocks without a page refresh.
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(interval);
  }, []);
  const canJoin = useMemo(() => {
    if (!booking) return false;
    return now >= new Date(booking.startDatetime).getTime() - JOIN_WINDOW_MS;
  }, [booking, now]);
  const isPolling = !error && !timedOut && (!booking || booking.status === "PAYMENT_PENDING" || booking.status === "PENDING" || booking.status === "SLOT_HOLD");
  return <div className="flex min-h-screen items-center justify-center bg-[#FAF8F5] px-4 py-10 font-sans text-slate-900">
      <div className="w-full max-w-lg rounded-3xl border border-[#EAE5DA] bg-white p-8 text-center shadow-[0_12px_30px_rgba(14,42,58,0.06)]">
        {error && <>
            <XCircle className="mx-auto h-14 w-14 text-red-400" />
            <h1 className="mt-4 font-heading text-xl font-semibold text-[#0E2A3A]">
              {isHindi ? "कुछ गलत हो गया" : "Something went wrong"}
            </h1>
            <p className="mt-2 text-sm text-slate-500">{error}</p>
          </>}

        {!error && isPolling && <>
            <Loader2 className="mx-auto h-14 w-14 animate-spin text-[#155A7D]" />
            <h1 className="mt-4 font-heading text-xl font-semibold text-[#0E2A3A]">
              {isHindi ? "भुगतान की पुष्टि हो रही है..." : "Confirming your payment..."}
            </h1>
            <p className="mt-2 text-sm text-slate-500">
              {isHindi ? "कृपया प्रतीक्षा करें, इसमें कुछ सेकंड लग सकते हैं।" : "Please hold on, this can take a few seconds."}
            </p>
          </>}

        {!error && timedOut && !isPolling && booking?.status !== "CONFIRMED" && <>
            <Clock className="mx-auto h-14 w-14 text-amber-500" />
            <h1 className="mt-4 font-heading text-xl font-semibold text-[#0E2A3A]">
              {isHindi ? "अभी भी संसाधित हो रहा है" : "Still processing"}
            </h1>
            <p className="mt-2 text-sm text-slate-500">
              {isHindi ? "भुगतान की पुष्टि में सामान्य से अधिक समय लग रहा है। कन्फर्म होने पर हम आपको सूचित करेंगे।" : "Confirmation is taking longer than usual. We'll notify you as soon as it's confirmed."}
            </p>
          </>}

        {!error && booking?.status === "PAYMENT_FAILED" && <>
            <XCircle className="mx-auto h-14 w-14 text-red-400" />
            <h1 className="mt-4 font-heading text-xl font-semibold text-[#0E2A3A]">
              {isHindi ? "भुगतान विफल रहा" : "Payment failed"}
            </h1>
            <p className="mt-2 text-sm text-slate-500">
              {isHindi ? "आपका भुगतान पूरा नहीं हो सका। कोई राशि नहीं काटी गई।" : "Your payment could not be completed. No amount was charged."}
            </p>
            <Link href={localizeHref(locale, `${healerBasePath}/${booking.healerId}`)} className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-[#155A7D] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#0E4663]">
              {isHindi ? "हीलर प्रोफ़ाइल पर वापस जाएं" : "Back to healer profile"}
            </Link>
          </>}

        {!error && booking?.status === "EXPIRED" && <>
            <Clock className="mx-auto h-14 w-14 text-amber-500" />
            <h1 className="mt-4 font-heading text-xl font-semibold text-[#0E2A3A]">
              {isHindi ? "समय समाप्त हो गया" : "This booking expired"}
            </h1>
            <p className="mt-2 text-sm text-slate-500">
              {isHindi ? "होल्ड/भुगतान की समय सीमा समाप्त हो गई। कृपया फिर से बुक करें।" : "The hold/payment window lapsed. Please start a new booking."}
            </p>
            <Link href={localizeHref(locale, `${healerBasePath}/${booking.healerId}`)} className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-[#155A7D] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#0E4663]">
              {isHindi ? "हीलर प्रोफ़ाइल पर वापस जाएं" : "Back to healer profile"}
            </Link>
          </>}

        {!error && booking?.status === "CONFIRMED" && <>
            <CheckCircle2 className="mx-auto h-14 w-14 text-[#7E9A88]" />
            <h1 className="mt-4 font-heading text-xl font-semibold text-[#0E2A3A]">
              {isHindi ? "आपका सत्र बुक हो गया!" : "Your session is confirmed!"}
            </h1>

            <div className="mt-6 space-y-2 rounded-2xl bg-[#FAF8F5] p-4 text-left text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">{isHindi ? "समय" : "When"}</span>
                <span className="font-medium text-slate-700">
                  {formatSlotDate(booking.startDatetime, locale)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">{isHindi ? "मोड" : "Mode"}</span>
                <span className="font-medium text-slate-700">
                  {isHindi ? MODE_LABEL[booking.mode]?.hi : MODE_LABEL[booking.mode]?.en}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">{isHindi ? "अवधि" : "Duration"}</span>
                <span className="font-medium text-slate-700">
                  {booking.durationMinutes} {isHindi ? "मिनट" : "min"}
                </span>
              </div>
            </div>

            <p className="mt-4 text-xs text-slate-400">
              {isHindi ? "आप अपने सत्र से 5 मिनट पहले जुड़ सकते हैं।" : "You can join your session 5 minutes before it starts."}
            </p>

            <div className="mt-6 flex flex-col gap-3">
              <button type="button" disabled={!canJoin} onClick={() => {
            if (booking.googleMeetLink) {
              window.open(booking.googleMeetLink, "_blank", "noopener,noreferrer");
            }
          }} className="flex items-center justify-center gap-2 rounded-full bg-[#155A7D] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#0E4663] disabled:cursor-not-allowed disabled:opacity-50">
                <Video className="h-4 w-4" />
                {isHindi ? "सत्र से जुड़ें" : "Join Session"}
              </button>

              <div className="flex gap-3">
                <button type="button" onClick={() => downloadIcs(booking)} className="flex flex-1 items-center justify-center gap-2 rounded-full border border-[#EAE5DA] px-5 py-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50">
                  <CalendarPlus className="h-4 w-4" />
                  {isHindi ? "कैलेंडर में जोड़ें" : "Add to Calendar"}
                </button>
                <a href={buildGoogleCalendarUrl({
              uid: `booking-${booking.id}@itshealer`,
              title: "ItsHealer session",
              startDatetime: booking.startDatetime,
              endDatetime: booking.endDatetime
            })} target="_blank" rel="noopener noreferrer" className="flex flex-1 items-center justify-center gap-2 rounded-full border border-[#EAE5DA] px-5 py-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50">
                  Google Calendar
                </a>
              </div>
            </div>
          </>}

        <div className="mt-8 flex items-start gap-2 rounded-xl bg-amber-50 px-4 py-3 text-left">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <p className="text-xs leading-5 text-amber-800">
            {isHindi ? "ItsHealer एक आपातकालीन सेवा नहीं है। यदि आप असुरक्षित या तत्काल खतरे में महसूस करते हैं, तो स्थानीय आपातकालीन सेवाओं या किसी विश्वसनीय व्यक्ति से संपर्क करें।" : "ItsHealer is not an emergency service. If you feel unsafe or in immediate danger, contact local emergency services or someone you trust."}
          </p>
        </div>
      </div>
    </div>;
}